import type Handlebars from 'handlebars'

type HandlebarsInstance = typeof Handlebars

/**
 * Helpers Handlebars liés aux produits.
 *
 * Usage :
 *   {{productUrl reference}}
 *   {{thumbnailUrl imageUrl 400}}
 *   {{#if (isAvailable availability)}} ... {{/if}}
 */
export function registerProductHelpers(hbs: HandlebarsInstance, siteUrl: string): void {
  // URL de la fiche produit côté site vitrine
  hbs.registerHelper('productUrl', function (reference: unknown): string {
    if (typeof reference !== 'string' && typeof reference !== 'number') return ''
    const base = siteUrl.replace(/\/$/, '')
    return `${base}/product/${encodeURIComponent(String(reference))}`
  })

  // Transforme une URL d'image en URL redimensionnée si le serveur le supporte
  // Pattern attendu : /media/images/{id} → /media/images/{id}?w={size}
  hbs.registerHelper('thumbnailUrl', function (imageUrl: unknown, size?: unknown): string {
    if (typeof imageUrl !== 'string' || !imageUrl) return ''
    const resolvedSize = typeof size === 'number' ? size : 300
    const separator = imageUrl.includes('?') ? '&' : '?'
    return `${imageUrl}${separator}w=${resolvedSize}`
  })

  // Retourne true si le produit est disponible (availability > 0 ou pas de stock géré)
  hbs.registerHelper('isAvailable', function (availability: unknown): boolean {
    if (availability === null || availability === undefined) return true
    if (typeof availability === 'boolean') return availability
    if (typeof availability === 'number') return availability > 0
    if (typeof availability === 'string') {
      const lower = availability.toLowerCase()
      return lower === 'available' || lower === 'instock'
    }
    return false
  })

  // Helper ifCond simple pour les comparaisons dans les templates
  hbs.registerHelper(
    'ifCond',
    function (
      this: unknown,
      v1: unknown,
      operator: string,
      v2: unknown,
      options: Handlebars.HelperOptions
    ): string {
      let result = false
      switch (operator) {
        case '==':  result = v1 == v2; break
        case '===': result = v1 === v2; break
        case '!=':  result = v1 != v2; break
        case '!==': result = v1 !== v2; break
        case '>':   result = (v1 as number) > (v2 as number); break
        case '<':   result = (v1 as number) < (v2 as number); break
        case '>=':  result = (v1 as number) >= (v2 as number); break
        case '<=':  result = (v1 as number) <= (v2 as number); break
        default:    result = false
      }
      return result ? options.fn(this as object) : options.inverse(this as object)
    }
  )
}
